import { useState, useEffect, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import DateUtils from "utils/DateUtils.js";
import PatientService from "services/PatientService.js";
import OrganizationService from "services/OrganizationService.js";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import MUIDataTable from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import DATA_TABLE_THEME from "themes/DataTableTheme.js";
import PopUpModal from "components/utils/PopUpModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import useAPI from "hooks/useAPI.js";
import FormUtils from "utils/FormUtils";
import FileUtils from "utils/FileUtils";
import { useSelector, useDispatch } from "react-redux";
import { selectHospital, focusHospital } from "state/slices/hospitalSlice";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";


export default function PatientList() {
    const dispatch = useDispatch();
    let navigate = useNavigate();

    const focusedHospital = useSelector(selectHospital);
    const hospitalId = focusedHospital.id;

    const [patientList, setPatientList] = useState([]);
    const popUpModalRef = useRef(null);
    const transferModalRef = useRef(null);
    const [archivingPatientId, setArchivingPatientId] = useState("");
    const [transferringPatientId, setTransferringPatientId] = useState("");
    const [transferringPatientName, setTransferringPatientName] = useState("");
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
    const [hospitals, setHospitals] = useState([]);
    const [selectedHospitalId, setSelectedHospitalId] = useState("");
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        total: 0,
        search: "",
    });
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;
    const [allCentres, setAllCentres] = useState(false);

    const optimisedSearchHandler = FormUtils.debounce(() => {
        if (allCentres) {
            fetchPatients(0);
        } else {
            fetchPatients(hospitalId);
        }
    }, 1000);

    const fetchPatientsAPI = useAPI(
        PatientService.getAllPatients,
        fetchPatientsHandler,
        false
    );
    const archivePatientAPI = useAPI(
        PatientService.archivePatient,
        archivePatientHandler,
        true
    );
    const sendRegistrationEmailAPI = useAPI(
        PatientService.sendRegistrationEmail,
        () => { },
        true
    );
    const getActiveOrganizationsAPI = useAPI(
        OrganizationService.getActiveOrganizations,
        getActiveOrganizationsHandler,
        false
    );
    const getHospitalsByOrganizationAPI = useAPI(
        OrganizationService.getHospitalsByOrganization,
        getHospitalsByOrganizationHandler,
        false
    );
    const transferPatientAPI = useAPI(
        PatientService.transferPatient,
        () => { },
        true
    );

    const dataTableOptions = {
        filter: false,
        responsive: "standard",
        selectableRows: "none",
        serverSide: true,
        download: true,
        downloadOptions: {
            filename: "Patients.csv",
            filterOptions: {
                useDisplayedColumnsOnly: true,
                useDisplayedRowsOnly: true,
            },
        },
        onDownload: () => {
            downloadAllPatientsData();
            return false;
        },
        jumpToPage: true,
        count: pagination.total,
        page: pagination.page,
        rowsPerPage: pagination.size,
        searchText: pagination.search,
        searchProps: {
            onKeyUp: optimisedSearchHandler,
        },
        onChangePage: (currentPage) => {
            pagination.page = currentPage;
            setPagination(pagination);
            fetchPatients(hospitalId);
        },
        onChangeRowsPerPage: (rowsPerPage) => {
            pagination.size = rowsPerPage;
            setPagination(pagination);
            fetchPatients(hospitalId);
        },
        onSearchChange: (searchText) => {
            const trimmed = searchText ? String(searchText).trim() : "";
            setPagination((prev) => ({
                ...prev,
                search: trimmed,
                page: 0,
            }));
        },
        onSearchClose: () => {
            if (pagination.search !== "") {
                pagination.search = "";
                setPagination(pagination);
                fetchPatients(hospitalId);
            }
        },
    };

    useEffect(() => {
        setAllCentres(false);

        fetchPatients(hospitalId);
    }, [hospitalId]);

    const fetchPatients = useCallback((hspId) => {
        const { page, size, search } = paginationRef.current;
        fetchPatientsAPI.execute(hspId, page, size, search);
    }, [fetchPatientsAPI]);

    async function downloadAllPatientsData() {
        const response = await PatientService.getAllPatients([hospitalId]);

        let patientsList = response.data.content;

        // remove redundant fields
        patientsList = patientsList.map((patient) => {
            delete patient.associatedHospitalId;
            delete patient.isArchived;
            delete patient.lastUpdatedAt;
            delete patient.profilePhotoUrl;

            return patient;
        });

        FileUtils.downloadCSV(patientsList, "Patients.csv");
    }

    function archivePatient(patientId) {
        archivePatientAPI.execute(hospitalId, patientId);
    }

    function sendRegistrationEmail(patientId) {
        sendRegistrationEmailAPI.execute(hospitalId, patientId);
    }

    /* API Success Handlers */
    function fetchPatientsHandler(response) {
        let data = response.data;

        setPagination((prev) => ({ ...prev, total: data.totalElements }));

        let patients = [];
        data.content.map((patient, index) =>
            patients.push(
                transformJsonData(
                    patient,
                    index + 1
                )
            )
        );
        setPatientList(patients);
    }

    function archivePatientHandler(response) {
        window.location.reload();
    }

    function getActiveOrganizationsHandler(response) {
        setOrganizations(response.data || []);
    }

    function getHospitalsByOrganizationHandler(response) {
        const hospitalsList = response.data || [];
        setHospitals(hospitalsList);
        // Reset selected hospital when organization changes
        setSelectedHospitalId("");
    }

    function transferPatient(patientId, patientName) {
        setTransferringPatientId(patientId);
        setTransferringPatientName(patientName || "");
        setSelectedOrganizationId("");
        setSelectedHospitalId("");
        setHospitals([]);
        getActiveOrganizationsAPI.execute();
        transferModalRef.current.openModal();
    }

    function handleOrganizationChange(orgId) {
        setSelectedOrganizationId(orgId);
        setSelectedHospitalId("");
        setHospitals([]);
        if (orgId) {
            getHospitalsByOrganizationAPI.execute(orgId);
        }
    }

    function handleTransferConfirm() {
        if (!selectedOrganizationId || !selectedHospitalId) {
            return;
        }
        transferPatientAPI.execute(hospitalId, transferringPatientId, {
            targetOrganizationId: selectedOrganizationId,
            targetHospitalId: selectedHospitalId,
        });
        transferModalRef.current.closeModal();
    }

    const handleAllCentresChange = (event) => {
        setAllCentres(event.target.checked);
        if (event.target.checked) {
            fetchPatients(0);
        } else {
            fetchPatients(hospitalId);
        }
    };

    return (
        <div>
            <Button

                className="float-end"
                onClick={() => navigate("add-patient")}
                startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
                variant="contained"
            >
                Add Patient
            </Button>
            <div className="d-flex align-items-center">
                <Checkbox
                    checked={allCentres}
                    onChange={handleAllCentresChange}
                    color="primary"
                />
                <label>All Centres</label>
            </div>
            {fetchPatientsAPI.status === "pending" ? (
                <div className="text-center">
                    <CircularProgress
                        className="text-center"
                        sx={{
                            mt: 50,
                        }}
                    />
                </div>
            ) : (
                <ThemeProvider theme={DATA_TABLE_THEME}>
                    <MUIDataTable
                        title={"Patients"}
                        data={patientList}
                        columns={dataTableColumns}
                        options={dataTableOptions}
                    />
                </ThemeProvider>
            )}
            <PopUpModal ref={popUpModalRef} modalWidth={400}>
                <ConfirmActionModal
                    content="Do you want to confirm archiving the patient?"
                    confirmActionHandler={() => {
                        archivePatient(archivingPatientId);
                        popUpModalRef.current.closeModal();
                    }}
                    onCloseHandler={() => popUpModalRef.current.closeModal()}
                />
            </PopUpModal>
            <PopUpModal ref={transferModalRef} modalWidth={500}>
                <Box
                    className="px-3 py-4"
                    sx={{
                        maxWidth: "450px",
                    }}
                >
                    <Typography id="modal-modal-title" variant="h3" color="text" sx={{ mb: 2 }}>
                        Transfer '{transferringPatientName || "Patient"}' to Another Organization
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Caution:</strong> Transferring a patient will create a new record in the selected organization and center, and
                        their data including profile, documents, biometrics, intake packs and registration forms, and assessment and stim forms
                        will be copied. This action will notify the patient via email. Please ensure you have selected the correct organization
                        and center.
                    </Alert>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="organization-select-label">Select Organization</InputLabel>
                        <Select
                            labelId="organization-select-label"
                            id="organization-select"
                            value={selectedOrganizationId}
                            label="Select Organization"
                            onChange={(e) => handleOrganizationChange(e.target.value)}
                        >
                            {organizations.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                    {org.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedOrganizationId && (
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="hospital-select-label">Select Hospital</InputLabel>
                            <Select
                                labelId="hospital-select-label"
                                id="hospital-select"
                                value={selectedHospitalId}
                                label="Select Hospital"
                                onChange={(e) => setSelectedHospitalId(e.target.value)}
                                disabled={hospitals.length === 0 || getHospitalsByOrganizationAPI.status === "pending"}
                            >
                                {hospitals.map((hospital) => (
                                    <MenuItem key={hospital.id} value={hospital.id}>
                                        {hospital.hospitalName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Stack direction="row" spacing={3} className="mt-4">
                        <Button
                            onClick={handleTransferConfirm}
                            variant="contained"
                            disabled={!selectedOrganizationId || !selectedHospitalId || transferPatientAPI.status === "pending"}
                        >
                            {transferPatientAPI.status === "pending" ? "Transferring..." : "Transfer"}
                        </Button>
                        <Button
                            onClick={() => transferModalRef.current.closeModal()}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </PopUpModal>
        </div>
    );

    function transformJsonData(
        patient,
        index
    ) {
        return {
            id: patient.id,
            index,
            fullName: patient.firstName + " " + patient.lastName,
            phone: patient.phone,
            email: patient.email,
            dob: patient.dob,
            createdAt: DateUtils.formatDateTime(patient.createdAt),
            actions: (
                <ActionsCell
                    patientId={patient.id}
                    hospitalId={patient.associatedHospitalId}
                    patientName={patient.firstName + " " + patient.lastName}
                />
            ),
        };
    }

    function ActionsCell({ patientId, hospitalId, patientName }) {
        const actions = {
            startItems: [
                {
                    component: <EditIcon fontSize="inherit" color="secondary" />,
                    onClickHandler: () => {
                        dispatch(focusHospital({ id: hospitalId }));
                        navigate(patientId + "/profile");
                    },
                },
                {
                    component: (
                        <Tooltip title="Archive Patient">
                            <ArchiveIcon fontSize="inherit" color="warning" />
                        </Tooltip>
                    ),
                    onClickHandler: () => {
                        setArchivingPatientId(patientId);
                        popUpModalRef.current.openModal();
                    },
                },
            ],
            menuItems: [
                {
                    label: "Send Registration Email",
                    onClickHandler: () => sendRegistrationEmail(patientId),
                },
                {
                    label: "Transfer Patient",
                    onClickHandler: () => transferPatient(patientId, patientName),
                },
            ],
        };

        return <TableRowActionsCell actions={actions} />;
    }
}

const dataTableColumns = [
    {
        name: "id",
        label: "Patient ID",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "fullName",
        label: "Full Name",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "email",
        label: "Email ID",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "phone",
        label: "Phone",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "createdAt",
        label: "Created At",
        options: {
            filter: true,
            sort: false,
        },
    },
    {
        name: "actions",
        label: "Actions",
        options: {
            filter: false,
            sort: false,
            download: false,
        },
    },
];
